import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { MapPin, Plus, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import type { Tool } from '@tools-beaver/shared';
import { apiClient } from '../api/client';
import { useAppStore } from '../store/useAppStore';
import toast from 'react-hot-toast';

const statusStyle: Record<string, string> = {
  AVAILABLE: 'bg-green-100 text-green-700',
  RENTED: 'bg-blue-100 text-blue-700',
  MAINTENANCE: 'bg-yellow-100 text-yellow-700',
  DRAFT: 'bg-silver-100 text-silver-500',
};

export function MyListings() {
  const { user, openAuthModal } = useAppStore();
  const queryClient = useQueryClient();

  const { data: tools, isLoading } = useQuery<Tool[]>({
    queryKey: ['myTools'],
    queryFn: async () => {
      const { data } = await apiClient.get('/tools/mine');
      return data.data;
    },
    enabled: !!user,
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ toolId, currentStatus }: { toolId: string; currentStatus: string }) => {
      const newStatus = currentStatus === 'AVAILABLE' ? 'MAINTENANCE' : 'AVAILABLE';
      await apiClient.patch(`/tools/${toolId}`, { status: newStatus });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myTools'] });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message ?? 'Failed to update'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (toolId: string) => apiClient.delete(`/tools/${toolId}`),
    onSuccess: () => {
      toast.success('Listing removed.');
      queryClient.invalidateQueries({ queryKey: ['myTools'] });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message ?? 'Failed to delete'),
  });

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <div className="text-5xl mb-3">✅</div>
        <p className="font-semibold text-silver-800 text-lg mb-2">My Listings</p>
        <p className="text-silver-500 mb-6">Sign in to manage your tool listings.</p>
        <button onClick={() => openAuthModal('login')} className="btn-primary">
          Sign In
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-10 text-center text-silver-400">
        <div className="text-4xl animate-bounce-gentle mb-3">✅</div>
        <p>Loading your listings…</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display font-extrabold text-2xl text-silver-800">My Listings</h1>
        <Link to="/list-tool" className="btn-primary flex items-center gap-2 text-sm px-4 py-2.5">
          <Plus size={16} /> Add Tool
        </Link>
      </div>

      {!tools?.length ? (
        <div className="text-center py-16 text-silver-400">
          <div className="text-5xl mb-3">🔧</div>
          <p className="font-medium mb-2">No listings yet</p>
          <p className="text-sm mb-6">
            List your tools and start earning from your neighbors!
          </p>
          <Link to="/list-tool" className="btn-primary inline-block">
            List Your First Tool
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {tools.map((tool) => (
            <div key={tool.id} className="card p-4 flex items-center gap-4">
              {/* Thumbnail */}
              <div className="w-16 h-16 rounded-2xl bg-silver-100 overflow-hidden flex-shrink-0">
                {tool.images?.[0]?.url ? (
                  <img
                    src={tool.images[0].url}
                    alt={tool.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-2xl">🔧</div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <Link
                  to={`/tools/${tool.id}`}
                  className="font-semibold text-silver-800 truncate hover:text-garden-600 transition-colors block"
                >
                  {tool.name}
                </Link>
                <div className="flex items-center gap-2 mt-1">
                  <span
                    className={`badge text-xs ${statusStyle[tool.status] ?? 'bg-silver-100 text-silver-600'}`}
                  >
                    {tool.status.replace('_', ' ')}
                  </span>
                  <span className="text-xs text-silver-400 font-medium">
                    ${Number(tool.pricePerDay).toFixed(0)}/day
                  </span>
                  {tool.city && (
                    <span className="text-xs text-silver-400 flex items-center gap-0.5">
                      <MapPin size={10} /> {tool.city}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {tool.status !== 'RENTED' && (
                  <button
                    onClick={() =>
                      toggleMutation.mutate({ toolId: tool.id, currentStatus: tool.status })
                    }
                    title={
                      tool.status === 'AVAILABLE' ? 'Mark as unavailable' : 'Mark as available'
                    }
                    className="p-2 rounded-xl hover:bg-silver-100 transition-colors text-silver-400 hover:text-garden-600"
                  >
                    {tool.status === 'AVAILABLE' ? (
                      <ToggleRight size={20} className="text-garden-500" />
                    ) : (
                      <ToggleLeft size={20} />
                    )}
                  </button>
                )}
                <button
                  onClick={() => {
                    if (confirm('Remove this listing?')) {
                      deleteMutation.mutate(tool.id);
                    }
                  }}
                  className="p-2 rounded-xl hover:bg-red-50 transition-colors text-silver-300 hover:text-red-500"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
